import { OptionRowsSkeleton } from '@/components/core';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui-kit/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetSkillsFor, useGetSkillTaxonomy } from '../../../hooks/use-skill-lookups';
import type { SelfSkillsValues } from '../../../schemas/employee-form-schema';
import { savedSkillIdsOf } from '../../../schemas/employee-form-schema';
import {
  createEmptySkillEntry,
  createEmptySubCategoryExperience,
  PROFICIENCY_LEVELS,
} from '../../../schemas/skills-schema';
import { skillsForSubCategories } from '../../../services/skill-lookups.service';
import { FormSection, HISTORY_YEARS } from '../form-layout';
import { FieldLabel } from '../required-fields';

/** A sub-category change waiting for the person to confirm that its skills go too. */
interface PendingSubCategoryChange {
  skillCount: number;
  apply: () => void;
}

/**
 * "Skills" step of the self-service profile: the person's skill category, the sub-categories they have
 * worked in (with the period) and the skills they have, each with a proficiency. Skills come from the
 * Skill Manager, and only those of the sub-categories recorded here can be picked; removing or changing
 * a sub-category removes its picked skills (after a confirmation). Old entries that match no skill
 * are kept as they are until the person changes them.
 */
export const SkillsStep = () => {
  const { t } = useTranslation();
  const { control, watch, setValue, getValues } = useFormContext<SelfSkillsValues>();
  const experiences = useFieldArray({ control, name: 'SubCategoryExperiences' });
  const skills = useFieldArray({ control, name: 'Skills' });
  const [pendingChange, setPendingChange] = useState<PendingSubCategoryChange | null>(null);
  // The saved entries' skills stay readable even outside the chosen sub-categories; skills picked later
  // come from those sub-categories, so the request only changes when a sub-category does.
  const [savedSkillIds] = useState(() => savedSkillIdsOf(getValues()));

  const { data: taxonomy } = useGetSkillTaxonomy();

  const chosenSubCategories = watch('SubCategoryExperiences').map((entry) => entry.SubCategoryId);
  const pickedSkills = watch('Skills');
  const chosenSkills = pickedSkills.map((entry) => entry.Skill);
  const recordedSubCategories = chosenSubCategories.filter(Boolean);

  const { data: skillOptions = [], isLoading: isSkillsLoading } = useGetSkillsFor(
    savedSkillIds,
    recordedSubCategories
  );
  const skillById = useMemo(
    () => new Map(skillOptions.map((skill) => [skill.ItemId, skill])),
    [skillOptions]
  );
  const subCategoryTitle = (id: string) =>
    taxonomy?.subCategories.find((subCategory) => subCategory.ItemId === id)?.Title ?? '';

  const availableSkills = skillsForSubCategories(skillOptions, recordedSubCategories);
  // With several sub-categories a skill name can repeat, so the option says which one it is.
  const showSubCategoryInLabel = recordedSubCategories.length > 1;
  const skillLabel = (skill: { Name: string; SubCategoryId: string }) =>
    showSubCategoryInLabel
      ? `${skill.Name} (${subCategoryTitle(skill.SubCategoryId)})`
      : skill.Name;
  const canAddSkill = availableSkills.some((skill) => !chosenSkills.includes(skill.ItemId));

  /** Indexes of the picked skills that belong to this sub-category. */
  const skillIndexesOf = (subCategoryId: string) =>
    subCategoryId
      ? pickedSkills.flatMap((entry, index) =>
          skillById.get(entry.Skill)?.SubCategoryId === subCategoryId ? [index] : []
        )
      : [];

  /** Runs `change` right away, or after a confirmation when it drops skills of `subCategoryId`. */
  const changeSubCategory = (subCategoryId: string, change: () => void) => {
    const indexes = skillIndexesOf(subCategoryId);
    if (indexes.length === 0) {
      change();
      return;
    }
    setPendingChange({
      skillCount: indexes.length,
      apply: () => {
        skills.remove(indexes);
        change();
      },
    });
  };

  /** The saved value stays selectable even when it is not (or no longer) among the options. */
  const withCurrent = <T extends { value: string; label: string }>(
    options: T[],
    current: string,
    label: string
  ) =>
    current && !options.some((option) => option.value === current)
      ? [...options, { value: current, label } as T]
      : options;

  return (
    <div className="flex flex-col gap-8">
      <FormSection title={t('CATEGORY')}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={control}
            name="CategoryId"
            render={({ field }) => (
              <FormItem>
                <FieldLabel>{t('CATEGORY')}</FieldLabel>
                <Select onValueChange={field.onChange} value={field.value || undefined}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('SELECT_SKILL_CATEGORY')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {withCurrent(
                      (taxonomy?.categories ?? []).map((category) => ({
                        value: category.ItemId,
                        label: category.Title,
                      })),
                      field.value,
                      field.value
                    ).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </FormSection>

      <FormSection title={t('SUB_CATEGORY')}>
        {experiences.fields.map((entry, index) => {
          const isUntilNow = watch(`SubCategoryExperiences.${index}.IsUntilNow`);
          const current = chosenSubCategories[index];
          const currentTitle = taxonomy?.subCategories.find(
            (subCategory) => subCategory.ItemId === current
          )?.Title;
          const subCategoryOptions = withCurrent(
            (taxonomy?.subCategories ?? [])
              // Every sub-category is offered, but each can only be added once.
              .filter(
                (subCategory) =>
                  subCategory.ItemId === current ||
                  !chosenSubCategories.includes(subCategory.ItemId)
              )
              .map((subCategory) => ({ value: subCategory.ItemId, label: subCategory.Title })),
            current,
            currentTitle ?? current
          );

          return (
            <div
              key={entry.id}
              className="grid grid-cols-1 items-start gap-4 rounded-lg border p-4 sm:grid-cols-2 xl:grid-cols-5"
            >
              <FormField
                control={control}
                name={`SubCategoryExperiences.${index}.SubCategoryId`}
                render={({ field }) => (
                  <FormItem className="xl:col-span-2">
                    <FieldLabel>{t('SUB_CATEGORY')}</FieldLabel>
                    <Select
                      onValueChange={(value) =>
                        changeSubCategory(field.value, () => field.onChange(value))
                      }
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('SELECT_SKILL_SUB_CATEGORY')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {subCategoryOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name={`SubCategoryExperiences.${index}.StartDate`}
                render={({ field }) => (
                  <FormItem>
                    <FieldLabel>{t('START_DATE')}</FieldLabel>
                    <FormControl>
                      <DatePicker
                        max={watch(`SubCategoryExperiences.${index}.EndDate`) || undefined}
                        yearsBack={HISTORY_YEARS}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name={`SubCategoryExperiences.${index}.EndDate`}
                render={({ field }) => (
                  <FormItem>
                    <FieldLabel>{t('END_DATE')}</FieldLabel>
                    <FormControl>
                      <DatePicker
                        min={watch(`SubCategoryExperiences.${index}.StartDate`) || undefined}
                        yearsBack={HISTORY_YEARS}
                        disabled={isUntilNow}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-between gap-2 sm:col-span-2 xl:col-span-1 xl:mt-[22px]">
                <FormField
                  control={control}
                  name={`SubCategoryExperiences.${index}.IsUntilNow`}
                  render={({ field }) => (
                    <FormItem>
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked === true);
                            if (checked === true)
                              setValue(`SubCategoryExperiences.${index}.EndDate`, '');
                          }}
                        />
                        {t('CURRENT')}
                      </label>
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-lg"
                  onClick={() => changeSubCategory(current, () => experiences.remove(index))}
                  aria-label={t('DELETE')}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          );
        })}

        <Button
          type="button"
          variant="outline"
          className="w-full border-dashed sm:w-auto sm:self-start"
          onClick={() => experiences.append(createEmptySubCategoryExperience())}
        >
          <Plus className="size-4" />
          {t('ADD_SUB_CATEGORY')}
        </Button>
      </FormSection>

      <FormSection title={t('SKILLS')}>
        {skills.fields.map((entry, index) => {
          const current = chosenSkills[index];
          const currentSkill = skillById.get(current);
          // An old entry (no current skill) is shown under the name it was saved with.
          const currentLabel = currentSkill
            ? skillLabel(currentSkill)
            : t('OLD_SKILL', { name: pickedSkills[index]?.SkillName || current });
          const options = withCurrent(
            availableSkills
              .filter((skill) => skill.ItemId === current || !chosenSkills.includes(skill.ItemId))
              .map((skill) => ({ value: skill.ItemId, label: skillLabel(skill) })),
            current,
            currentLabel
          );
          // HR can move a skill to another sub-category after the person picked it.
          const isOutsideSubCategories =
            currentSkill !== undefined &&
            !recordedSubCategories.includes(currentSkill.SubCategoryId);

          return (
            <div
              key={entry.id}
              className="grid grid-cols-1 items-start gap-4 rounded-lg border p-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]"
            >
              <FormField
                control={control}
                name={`Skills.${index}.Skill`}
                render={({ field }) => (
                  <FormItem>
                    <FieldLabel>{t('SKILL')}</FieldLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setValue(`Skills.${index}.SkillName`, skillById.get(value)?.Name ?? '');
                      }}
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('SELECT_SKILL')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {isSkillsLoading && <OptionRowsSkeleton />}
                        {options.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isOutsideSubCategories && (
                      <p className="text-xs text-muted-foreground">
                        {t('SKILL_NOT_IN_YOUR_SUB_CATEGORIES')}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name={`Skills.${index}.Proficiency`}
                render={({ field }) => (
                  <FormItem>
                    <FieldLabel>{t('PROFICIENCY_LEVEL')}</FieldLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      value={String(field.value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PROFICIENCY_LEVELS.map((level) => (
                          <SelectItem key={level.key} value={String(level.key)}>
                            {level.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="button"
                variant="ghost"
                size="icon-lg"
                className="justify-self-end sm:mt-[22px] sm:justify-self-auto"
                onClick={() => skills.remove(index)}
                aria-label={t('DELETE')}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          );
        })}

        {recordedSubCategories.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('ADD_SUB_CATEGORY_TO_PICK_SKILLS')}</p>
        ) : (
          // Not "no skills" while the new sub-category's skills are still loading.
          !isSkillsLoading &&
          availableSkills.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('NO_SKILLS_IN_SUB_CATEGORIES')}</p>
          )
        )}

        <Button
          type="button"
          variant="outline"
          className="w-full border-dashed sm:w-auto sm:self-start"
          disabled={!canAddSkill}
          onClick={() => skills.append(createEmptySkillEntry())}
        >
          <Plus className="size-4" />
          {t('ADD_SKILL')}
        </Button>
      </FormSection>

      <ConfirmationModal
        open={pendingChange !== null}
        onOpenChange={(open) => {
          if (!open) setPendingChange(null);
        }}
        title={t('REMOVE_SUB_CATEGORY_SKILLS_TITLE')}
        description={t('REMOVE_SUB_CATEGORY_SKILLS_CONFIRM', {
          count: pendingChange?.skillCount ?? 0,
        })}
        confirmText={t('CONTINUE')}
        onConfirm={() => {
          pendingChange?.apply();
          setPendingChange(null);
        }}
      />
    </div>
  );
};
